import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getCsrfToken } from '@/utils/api';
import { Editor } from '@/ui/Editor';

type GalleryPost = { id: number; title: string; slug: string };
type Announcement = { id: number; title: string; slug: string };

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const csrf = getCsrfToken();

  // Gallery state
  const [galleryPosts, setGalleryPosts] = useState<GalleryPost[]>([]);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [galleryCover, setGalleryCover] = useState<File | null>(null);
  const [galleryImages, setGalleryImages] = useState<File[]>([]);
  const [galleryDescription, setGalleryDescription] = useState('');
  const [savingGallery, setSavingGallery] = useState(false);

  // Announcement state
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [annTitle, setAnnTitle] = useState('');
  const [annContent, setAnnContent] = useState('<p></p>');
  const [savingAnn, setSavingAnn] = useState(false);

  useEffect(() => {
    document.title = 'Yönetim Paneli | ESDFL MUN';
  }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const [galRes, annRes] = await Promise.all([
          api.get('/api/admin/gallery'),
          api.get('/api/admin/announcements')
        ]);
        if (!ignore) {
          setGalleryPosts(galRes.posts ?? []);
          setAnnouncements(annRes.announcements ?? []);
        }
      } catch {
        navigate('/__admin');
      }
    })();
    return () => { ignore = true; };
  }, [navigate]);

  const uploadFile = async (file: File): Promise<{ url: string; thumbUrl?: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.upload('/api/admin/upload', fd, csrf);
    return { url: res.url, thumbUrl: res.thumbUrl };
  };

  const onCreateGallery = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingGallery(true);
    try {
      let coverImageUrl: string | undefined;
      let coverThumbUrl: string | undefined;
      if (galleryCover) {
        const uploaded = await uploadFile(galleryCover);
        coverImageUrl = uploaded.url;
        coverThumbUrl = uploaded.thumbUrl;
      }
      const imageUrls: string[] = [];
      const thumbUrls: string[] = [];
      for (const img of galleryImages) {
        const uploaded = await uploadFile(img);
        imageUrls.push(uploaded.url);
        if (uploaded.thumbUrl) thumbUrls.push(uploaded.thumbUrl);
      }
      const res = await api.post('/api/admin/gallery', {
        title: galleryTitle,
        coverImageUrl,
        coverThumbUrl,
        images: imageUrls,
        thumbs: thumbUrls,
        description: galleryDescription || undefined
      }, csrf);
      setGalleryPosts((p) => [{ id: res.id, title: galleryTitle, slug: res.slug }, ...p]);
      setGalleryTitle('');
      setGalleryCover(null);
      setGalleryImages([]);
      setGalleryDescription('');
      alert('Galeri oluşturuldu!');
    } finally {
      setSavingGallery(false);
    }
  };

  const onDeleteGallery = async (id: number) => {
    if (!confirm('Bu galeriyi silmek istediğinize emin misiniz?')) return;
    await api.del(`/api/admin/gallery/${id}`, undefined, csrf);
    setGalleryPosts((p) => p.filter((x) => x.id !== id));
  };

  const onCreateAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingAnn(true);
    try {
      const res = await api.post('/api/admin/announcements', {
        title: annTitle,
        contentHtml: annContent
      }, csrf);
      setAnnouncements((a) => [{ id: res.id, title: annTitle, slug: res.slug }, ...a]);
      setAnnTitle('');
      setAnnContent('<p></p>');
      alert('Duyuru yayınlandı!');
    } finally {
      setSavingAnn(false);
    }
  };

  const onDeleteAnnouncement = async (id: number) => {
    if (!confirm('Bu duyuruyu silmek istediğinize emin misiniz?')) return;
    await api.del(`/api/admin/announcements/${id}`, undefined, csrf);
    setAnnouncements((a) => a.filter((x) => x.id !== id));
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">MUN Yönetim Paneli</h1>
        <div className="flex items-center gap-2">
          <form onSubmit={async (e) => { e.preventDefault(); await api.post('/api/admin/logout', {}, csrf); navigate('/__admin'); }}>
            <button className="btn-secondary" type="submit">Çıkış Yap</button>
          </form>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gallery Section */}
        <section className="lg:col-span-2 card p-4">
          <h2 className="font-semibold mb-4">Yeni Galeri</h2>
          <form onSubmit={onCreateGallery} className="space-y-4">
            <div>
              <label className="label">Başlık</label>
              <input className="input" value={galleryTitle} onChange={(e) => setGalleryTitle(e.target.value)} placeholder="Galeri başlığı" required />
            </div>
            <div>
              <label className="label">Kapak Görseli</label>
              <input className="input" type="file" accept="image/*" onChange={(e) => setGalleryCover(e.target.files?.[0] ?? null)} />
            </div>
            <div>
              <label className="label">Diğer Görseller</label>
              <input className="input" type="file" accept="image/*" multiple onChange={(e) => setGalleryImages(Array.from(e.target.files ?? []))} />
              {galleryImages.length > 0 && <p className="text-sm text-gray-500 mt-1">{galleryImages.length} görsel seçildi</p>}
            </div>
            <div>
              <label className="label">Açıklama</label>
              <textarea className="input" value={galleryDescription} onChange={(e) => setGalleryDescription(e.target.value)} placeholder="Açıklama (isteğe bağlı)" rows={3} />
            </div>
            <button type="submit" className="btn" disabled={savingGallery}>{savingGallery ? 'Oluşturuluyor…' : 'Galeri Oluştur'}</button>
          </form>
        </section>

        <aside className="card p-4">
          <h2 className="font-semibold mb-4">Galeriler</h2>
          <ul className="space-y-2 max-h-48 overflow-auto">
            {galleryPosts.map((p) => (
              <li key={p.id} className="flex items-center justify-between">
                <span className="truncate" title={p.title}>{p.title}</span>
                <button className="btn-secondary" type="button" onClick={() => onDeleteGallery(p.id)}>Sil</button>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Announcement Section */}
        <section className="lg:col-span-2 card p-4">
          <h2 className="font-semibold mb-4">Yeni Duyuru</h2>
          <form onSubmit={onCreateAnnouncement} className="space-y-4">
            <div>
              <label className="label">Başlık</label>
              <input className="input" value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} placeholder="Duyuru başlığı" required />
            </div>
            <div>
              <label className="label">İçerik</label>
              <Editor value={annContent} onChange={setAnnContent} />
            </div>
            <button type="submit" className="btn" disabled={savingAnn}>{savingAnn ? 'Yayınlanıyor…' : 'Yayınla'}</button>
          </form>
        </section>

        <aside className="card p-4">
          <h2 className="font-semibold mb-4">Duyurular</h2>
          <ul className="space-y-2 max-h-72 overflow-auto pr-1">
            {announcements.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3">
                <span className="truncate" title={a.title}>{a.title}</span>
                <button className="btn-secondary" type="button" onClick={() => onDeleteAnnouncement(a.id)}>Sil</button>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
